import { useTranslations } from '@vocab/react';
import { Box, Button, IconAI } from 'braid-design-system';

import { useSignedInDashboardContext } from '../../SignedInDashboardContextProvider';
import { CareerFeedAiAssistantInitialState } from '../CareerFeedAiAssistantInitialState/CareerFeedAiAssistantInitialState';
import { CareerFeedAssistantQuestions } from '../CareerFeedAssistantQuestions/CareerFeedAssistantQuestions';
import {
  useGetRefreshRecommendationsWithCandidateChatContext,
  useGetResetCandidateChatContext,
} from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';

import translations from './.vocab';
import { CareerFeedAIAssistantContentFrame } from './CareerFeedAIAssistantContentFrame';

import * as styleRefs from './CareerFeedAiAssistantContentFrame.css';

const ConversationFinished = () => {
  const { t } = useTranslations(translations);

  const { lastCompletedAssistantResponse } = useCareerFeedAIAssistant();
  const {
    recommendationsResult: { isRefreshLoading },
  } = useSignedInDashboardContext();

  const refreshRecommendations =
    useGetRefreshRecommendationsWithCandidateChatContext();

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={lastCompletedAssistantResponse!.text}
      ctaSection={
        <Button
          tone="brandAccent"
          icon={<IconAI />}
          onClick={refreshRecommendations}
          loading={isRefreshLoading}
        >
          {t('Refresh recommendations')}
        </Button>
      }
    />
  );
};

const RecommendationRefinedConfirmation = () => {
  const resetPreferences = useGetResetCandidateChatContext();
  const { t } = useTranslations(translations);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={t(
        "We've updated your recommendations based on your answers. Take a look at the personalised job listings.",
      )}
      ctaSection={
        <Box className={styleRefs.disableStretch}>
          <Button size="small" onClick={resetPreferences}>
            {t('Reset my preferences')}
          </Button>
        </Box>
      }
    />
  );
};

export const CareerFeedAIAssistantContent = () => {
  const { progress } = useCareerFeedAIAssistant();

  if (
    progress === 'recsRefined' ||
    progress === 'recsRefined-withPersistedData'
  ) {
    return <RecommendationRefinedConfirmation />;
  }

  if (progress === 'reviewRefinement') {
    return <ConversationFinished />;
  }

  if (progress === 'initial') {
    return <CareerFeedAiAssistantInitialState />;
  }

  return <CareerFeedAssistantQuestions />;
};
